import { memo, useCallback, useMemo } from 'react';

import { useSearchParams } from 'react-router-dom';
import { updateSearchParams } from '@library';

import Container from '../../core/components/Container';
import useStoreAndLanguageParams from '../../core/hooks/useStoreAndLanguageParams';
import useCustomNavigate from '../../core/hooks/router/useCustomNavigate';
import type { MotorcycleSearchParams } from './MotorcycleSearch';
import MotorcycleSearch from './MotorcycleSearch';

export interface VehicleFilters {
    make: string[];
    model: string[];
    ccm: string[];
    ratedPower: string[];
    modelYear: string[];
    type: string[];
    typeApprovalList: string[];
}

const useMotorcyclePageParams = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const params = useMemo(() => {
        const search = searchParams.get('search') ?? '';
        const page = searchParams.get('page') ?? '1';
        const make = searchParams.get('make') ?? undefined;
        const model = searchParams.get('model') ?? undefined;
        const ccm = searchParams.get('ccm') ?? undefined;
        const ratedPower = searchParams.get('ratedPower') ?? undefined;
        const modelYear = searchParams.get('ModelYear') ?? undefined;
        const type = searchParams.get('type') ?? undefined;
        const typeApprovalList = searchParams.getAll('typeApprovalList');
        const licensePlate = searchParams.get('licensePlate') ?? '';
        return {
            search,
            page: +page,
            make,
            model,
            ccm,
            ratedPower,
            modelYear,
            type,
            typeApprovalList,
            licensePlate,
        } as MotorcycleSearchParams;
    }, [searchParams]);

    const updateParams = (newParams: Partial<typeof params>) => {
        setSearchParams(updateSearchParams(searchParams, newParams), {
            replace: true,
            preventScrollReset: true,
        });
    };

    return {
        params,
        updateParams: useCallback(updateParams, [
            searchParams,
            setSearchParams,
        ]),
    };
};

const MotorcycleSearchPage = () => {
    const { storeRootPath } = useStoreAndLanguageParams();
    const navigate = useCustomNavigate();
    const { params, updateParams } = useMotorcyclePageParams();

    const handleModelChange = (vehicleId: string) => {
        navigate({
            pathname: `${storeRootPath}/vehicle-search`,
            search: `?vehicleId=${vehicleId}`,
        });
    };

    return (
        <Container pageContent>
            <MotorcycleSearch
                params={params}
                updateParams={updateParams}
                onModelChange={handleModelChange}
            />
        </Container>
    );
};

export default memo(MotorcycleSearchPage);
